<route lang="json">
{
  "path": "/:locale(en|sv)",
  "name": "home-locale",
  "meta": { "beige": true }
}
</route>

<script setup lang="ts">
import { useHead } from "@unhead/vue";
import type { Locale } from "shared-core/services/i18n.ts";
import mascotImage from "shared-ui/assets/illustrations/binoculars-person-orange.svg";
import googleWorkspaceLogo from "shared-ui/assets/system-logos/google_workspace.svg";
import microsoft365Logo from "shared-ui/assets/system-logos/microsoft_365.svg";
import wordpressLogo from "shared-ui/assets/system-logos/wordpress.svg";
import CTAButton from "shared-ui/components/buttons/CTAButton.vue";
import DecorShape, {
  type DecorShapeKind,
} from "shared-ui/components/decor/DecorShape.vue";
import DecorStrip from "shared-ui/components/decor/DecorStrip.vue";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import CustomerLogoMarquee from "@/components/CustomerLogoMarquee.vue";
import { type BlogPost, getAllPostsForLocale } from "@/services/blog.ts";
import { useSsrHref } from "@/utils/router-ssr-href.ts";
import IconMaterialSymbolsAddRounded from "~icons/material-symbols/add-rounded";
import IconMaterialSymbolsCheckCircle from "~icons/material-symbols/check-circle";
import IconMaterialSymbolsInfoOutline from "~icons/material-symbols/info-outline";

const { t, locale } = useI18n();

const homeTitle = computed(() => t("home.title"));
const homeDescription = computed(() => t("home.meta_description"));

useHead({
  title: homeTitle,
  titleTemplate: "%s",
  meta: [
    { property: "og:title", content: homeTitle },
    { name: "description", content: homeDescription },
    { property: "og:description", content: homeDescription },
  ],
});

const ssrHref = useSsrHref();
const appBase: string = import.meta.env.VITE_PUBLIC_APP_SITE_URL;

const productHref = computed(() =>
  ssrHref({ name: "product-locale", params: { locale: locale.value } }),
);
const resourcesHref = computed(() =>
  ssrHref({ name: "resources-locale", params: { locale: locale.value } }),
);

type DecorItem = {
  shape: DecorShapeKind;
  color: string;
  styles: Record<string, string>;
};

const compactHeroDecor: DecorItem[] = [
  {
    shape: "star",
    color: "#F5B301",
    styles: { left: "5%", top: "1.5rem", width: "24px", height: "24px" },
  },
  {
    shape: "dot",
    color: "#E8663A",
    styles: { left: "26%", top: "0.75rem", width: "9px", height: "9px" },
  },
  {
    shape: "triangle-right",
    color: "#1E7BD8",
    styles: {
      left: "46%",
      top: "2rem",
      width: "16px",
      height: "16px",
      rotate: "331deg",
    },
  },
  {
    shape: "ring",
    color: "#191919",
    styles: { right: "24%", top: "0.75rem", width: "12px", height: "12px" },
  },
  {
    shape: "star",
    color: "#1E8F57",
    styles: { right: "5%", top: "2rem", width: "20px", height: "20px" },
  },
  {
    shape: "ring",
    color: "#191919",
    styles: { left: "6%", bottom: "1rem", width: "14px", height: "14px" },
  },
  {
    shape: "star",
    color: "#E8663A",
    styles: { right: "8%", bottom: "0.75rem", width: "20px", height: "20px" },
  },
];

// Hero decor in the Paper style: the same shape/colour palette
// scattered around the heading. Positioned *behind* the copy with fixed top
// offsets (kept well within the hero so nothing is clipped on wide screens)
// and gutter/edge percentages, so pieces sit in the margins and top band
// without ever covering the text.
const heroDecor: DecorItem[] = [
  // Top band above the heading, spread across the full width
  {
    shape: "dot",
    color: "#191919",
    styles: { left: "21%", top: "0.5rem", width: "8px", height: "8px" },
  },
  {
    shape: "triangle-right",
    color: "#1E7BD8",
    styles: {
      left: "46%",
      top: "1rem",
      width: "13px",
      height: "13px",
      rotate: "331deg",
    },
  },
  {
    shape: "star",
    color: "#F5B301",
    styles: { left: "63%", top: "0.5rem", width: "18px", height: "18px" },
  },
  {
    shape: "dot",
    color: "#E8663A",
    styles: { left: "80%", top: "1.25rem", width: "10px", height: "10px" },
  },
  // Left side, staggered across the gutter (x 3-22%) at irregular heights
  {
    shape: "star",
    color: "#F5B301",
    styles: { left: "4%", top: "5rem", width: "26px", height: "26px" },
  },
  {
    shape: "triangle-up",
    color: "#1E8F57",
    styles: { left: "21%", top: "1.5rem", width: "16px", height: "16px" },
  },
  {
    shape: "ring",
    color: "#191919",
    styles: { left: "11%", top: "9.5rem", width: "15px", height: "15px" },
  },
  {
    shape: "triangle-right",
    color: "#1E7BD8",
    styles: {
      left: "18%",
      top: "13rem",
      width: "14px",
      height: "14px",
      rotate: "331deg",
    },
  },
  {
    shape: "star-outline",
    color: "#191919",
    styles: { left: "3%", top: "14.5rem", width: "24px", height: "24px" },
  },
  {
    shape: "dot",
    color: "#E8663A",
    styles: { left: "9%", top: "17rem", width: "13px", height: "13px" },
  },
  // Right side, staggered across the gutter (x 3-22%) at irregular heights
  {
    shape: "star",
    color: "#E8663A",
    styles: { right: "20%", top: "2rem", width: "15px", height: "15px" },
  },
  {
    shape: "star",
    color: "#F5B301",
    styles: { right: "4%", top: "6.5rem", width: "36px", height: "36px" },
  },
  {
    shape: "ring",
    color: "#191919",
    styles: { right: "15%", top: "10.5rem", width: "15px", height: "15px" },
  },
  {
    shape: "dot",
    color: "#1E7BD8",
    styles: { right: "22%", top: "13.5rem", width: "12px", height: "12px" },
  },
  {
    shape: "triangle-up",
    color: "#1E8F57",
    styles: { right: "7%", top: "16rem", width: "20px", height: "20px" },
  },
];

// Shapes for the "more than a scan" feature columns, matching the Paper export.
const moreFeatures: { key: string; shape: DecorShapeKind; color: string }[] = [
  { key: "action", shape: "star", color: "#F5B301" },
  { key: "whole_business", shape: "triangle-up", color: "#1E8F57" },
  { key: "keep_working", shape: "dot", color: "#E8663A" },
];

const teamMembers = [
  { initials: "AO", name: "Agnes Onne", role: "role_admin", status: "active" },
  { initials: "JL", name: "Johan Lund", role: "role_user", status: "active" },
  { initials: "SB", name: "Sara Berg", role: "role_user", status: "invited" },
] as const;

const blogPosts = computed<BlogPost[]>(() => {
  const all = getAllPostsForLocale(locale.value as Locale);

  return all
    .sort((a, b) => Date.parse(b.date) - Date.parse(a.date))
    .slice(0, 4);
});
</script>

<template>
  <!-- ═══════════════ Hero ═══════════════ -->
  <section
    class="relative overflow-clip px-6 pt-24 pb-18 website-hero-decor:pt-22"
  >
    <div
      aria-hidden="true"
      class="pointer-events-none absolute inset-0 website-hero-decor:hidden"
    >
      <DecorShape
        v-for="(c, i) in compactHeroDecor"
        :key="i"
        :shape="c.shape"
        :color="c.color"
        :styles="c.styles"
      />
    </div>
    <!-- Decorative shapes: Paper-style scatter behind the copy, confined to
         this section so it never reaches the logo carousel below. lg+. -->
    <div
      aria-hidden="true"
      class="pointer-events-none absolute inset-0 hidden website-hero-decor:block"
    >
      <DecorShape
        v-for="(c, i) in heroDecor"
        :key="i"
        :shape="c.shape"
        :color="c.color"
        :styles="c.styles"
      />
    </div>

    <div class="relative mx-auto flex w-full max-w-3xl flex-col items-center">
      <h1
        class="mobile-page-heading text-center sm:text-7xl! sm:leading-website-hero sm:font-normal!"
      >
        {{ t("home.hero.header") }}
      </h1>
      <p
        class="mt-6 max-w-2xl text-center text-lg text-secondary-black sm:text-xl"
      >
        {{ t("home.hero.sub_header") }}
      </p>

      <div
        class="mt-9 flex w-full max-w-xs flex-col items-stretch gap-3 sm:max-w-none sm:flex-row sm:flex-wrap sm:items-center sm:justify-center"
      >
        <CTAButton
          :message="t('cta.start_trial')"
          :link="appBase"
          link-medium="home"
          link-campaign="hero_start_trial"
          link-class="!bg-brand-blue !px-8 !text-base !w-full sm:!w-auto"
        />
        <CTAButton
          :message="t('cta.see_how_it_works')"
          :link="productHref"
          target="_self"
          link-medium="home"
          link-campaign="hero_see_how_it_works"
          link-class="!bg-white border border-lines !px-8 !text-primary-black !text-base !w-full sm:!w-auto"
        />
      </div>

      <ul
        class="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2"
      >
        <li
          v-for="key in ['no_card', 'setup_minutes', 'easy_to_use']"
          :key="key"
          class="flex items-center gap-1.5"
        >
          <IconMaterialSymbolsCheckCircle class="size-4.5 text-brand-green" />
          <span class="text-sm font-medium text-medium-gray">{{
            t(`home.hero.${key}`)
          }}</span>
        </li>
      </ul>
    </div>
  </section>

  <!-- ═══════════════ Client logo carousel (reused from the product page) ═══ -->
  <section class="pt-6 pb-14 sm:pt-10">
    <div class="flex w-full flex-col items-center gap-8">
      <p class="px-6 text-center text-sm font-semibold text-website-muted">
        {{ t("home.hero.trusted_by") }}
      </p>
      <CustomerLogoMarquee class="opacity-70" />
    </div>
  </section>

  <!-- ═══════════════ Five simple steps ═══════════════ -->
  <section class="bg-white px-6 pt-10 pb-24">
    <div class="mx-auto flex w-full max-w-7xl flex-col items-center">
      <h2
        class="max-w-3xl text-center text-4xl! leading-snug text-balance text-primary-black! md:text-5xl!"
      >
        {{ t("home.steps.header") }}
      </h2>
      <p
        class="mt-5 max-w-2xl text-center text-lg whitespace-pre-line text-secondary-black"
      >
        {{ t("home.steps.body") }}
      </p>

      <div class="mt-16 flex w-full flex-col gap-16 lg:gap-20">
        <!-- Step 1: Connect -->
        <div
          class="flex flex-col items-center gap-8 lg:flex-row lg:items-center lg:justify-center lg:gap-16"
        >
          <div class="flex w-full flex-col lg:w-lg lg:shrink-0">
            <span class="text-sm font-semibold text-brand-blue">{{
              t("home.steps.label", { number: 1 })
            }}</span>
            <h3
              class="mt-4 font-serif text-3xl font-medium text-primary-black md:text-4xl"
            >
              {{ t("home.steps.connect.title") }}
            </h3>
            <p class="mt-3.5 text-lg text-secondary-black">
              {{ t("home.steps.connect.body") }}
            </p>
          </div>
          <!-- Card: Connected services -->
          <div
            class="w-full rounded-2xl border border-brand-dark-beige bg-white p-6 shadow-card-prominent lg:w-108 lg:shrink-0"
          >
            <p class="mb-4 font-semibold text-primary-black">
              {{ t("home.steps.connect.card_title") }}
            </p>
            <div
              class="flex items-center gap-3.5 border-b border-card-line py-3"
            >
              <div
                class="flex size-9 shrink-0 items-center justify-center rounded-website-integration-icon bg-card-line p-1.5"
              >
                <img
                  :src="microsoft365Logo"
                  alt="Microsoft 365"
                  class="size-full object-contain"
                />
              </div>
              <span class="grow font-medium text-primary-black"
                >Microsoft 365</span
              >
              <span
                class="flex items-center gap-1.5 text-website-detail font-semibold text-brand-green"
              >
                <IconMaterialSymbolsCheckCircle class="size-4.5" />
                {{ t("home.steps.connect.connected") }}
              </span>
            </div>
            <div
              class="flex items-center gap-3.5 border-b border-card-line py-3"
            >
              <div
                class="flex size-9 shrink-0 items-center justify-center rounded-website-integration-icon bg-card-line p-1.5"
              >
                <img
                  :src="googleWorkspaceLogo"
                  alt="Google Workspace"
                  class="size-full object-contain"
                />
              </div>
              <span class="grow font-medium text-primary-black"
                >Google Workspace</span
              >
              <span
                class="flex items-center gap-1.5 text-website-detail font-semibold text-brand-green"
              >
                <IconMaterialSymbolsCheckCircle class="size-4.5" />
                {{ t("home.steps.connect.connected") }}
              </span>
            </div>
            <div class="flex items-center gap-3.5 py-3">
              <div
                class="flex size-9 shrink-0 items-center justify-center rounded-website-integration-icon bg-card-line p-1.5"
              >
                <img
                  :src="wordpressLogo"
                  alt="WordPress"
                  class="size-full object-contain"
                />
              </div>
              <span class="grow font-medium text-primary-black">WordPress</span>
              <span
                class="flex h-7 items-center rounded-pill bg-blue-accent-light px-3.5 text-website-detail font-semibold text-brand-blue"
              >
                {{ t("home.steps.connect.connect") }}
              </span>
            </div>
          </div>
        </div>

        <!-- Step 2: Overview -->
        <div
          class="flex flex-col items-center gap-8 lg:flex-row-reverse lg:items-center lg:justify-center lg:gap-16"
        >
          <div class="flex w-full flex-col lg:w-lg lg:shrink-0">
            <span class="text-sm font-semibold text-brand-blue">{{
              t("home.steps.label", { number: 2 })
            }}</span>
            <h3
              class="mt-4 font-serif text-3xl font-medium text-primary-black md:text-4xl"
            >
              {{ t("home.steps.overview.title") }}
            </h3>
            <p class="mt-3.5 text-lg text-secondary-black">
              {{ t("home.steps.overview.body") }}
            </p>
          </div>
          <!-- Card: rating -->
          <div
            class="flex w-full flex-col gap-5 rounded-2xl border border-brand-dark-beige bg-white p-7 shadow-card-prominent lg:w-108 lg:shrink-0"
          >
            <div>
              <div class="flex items-center justify-between">
                <p class="text-sm font-semibold text-primary-black">
                  {{ t("home.steps.overview.rating_title") }}
                </p>
                <IconMaterialSymbolsInfoOutline
                  class="size-4.5 text-website-subtle"
                />
              </div>
              <div class="mt-4 flex items-center gap-1.5">
                <span
                  v-for="grade in ['F', 'E', 'D', 'C', 'B', 'A']"
                  :key="grade"
                  class="flex items-center justify-center rounded-full font-semibold text-white"
                  :class="
                    grade === 'D'
                      ? 'size-10 bg-dark-yellow text-lg'
                      : 'size-7.5 bg-website-rating-inactive text-sm'
                  "
                  >{{ grade }}</span
                >
              </div>
              <p class="mt-3.5 text-xs/relaxed text-medium-gray">
                {{ t("home.steps.overview.rating_body") }}
              </p>
            </div>
            <div class="h-px w-full bg-card-line"></div>
            <div>
              <div class="flex items-center justify-between gap-2">
                <p class="text-sm font-semibold text-primary-black">
                  {{ t("home.steps.overview.insurance_title") }}
                </p>
                <IconMaterialSymbolsInfoOutline
                  class="size-4.5 text-website-subtle"
                />
              </div>
              <div class="mt-3 flex items-baseline gap-1.5">
                <span class="font-serif text-4xl text-primary-black">1</span>
                <span class="text-base text-website-muted">/ 7</span>
              </div>
              <div
                class="mt-3 h-2.5 overflow-clip rounded-pill bg-website-progress-track"
              >
                <div class="h-full w-7/50 rounded-pill bg-brand-green"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Step 3: Fix -->
        <div
          class="flex flex-col items-center gap-8 lg:flex-row lg:items-center lg:justify-center lg:gap-16"
        >
          <div class="flex w-full flex-col lg:w-lg lg:shrink-0">
            <span class="text-sm font-semibold text-brand-blue">{{
              t("home.steps.label", { number: 3 })
            }}</span>
            <h3
              class="mt-4 font-serif text-3xl font-medium text-primary-black md:text-4xl"
            >
              {{ t("home.steps.fix.title") }}
            </h3>
            <p class="mt-3.5 text-lg text-secondary-black">
              {{ t("home.steps.fix.body") }}
            </p>
          </div>
          <!-- Card: fix task -->
          <div
            class="flex w-full flex-col rounded-2xl border border-brand-dark-beige bg-white p-7 shadow-card-prominent lg:w-108 lg:shrink-0"
          >
            <div class="flex items-center gap-3">
              <div
                class="grid size-5 shrink-0 grid-cols-2 gap-website-logo-gap"
              >
                <span class="rounded-website-logo-pixel bg-admin-label"></span>
                <span class="rounded-website-logo-pixel bg-admin-label"></span>
                <span class="rounded-website-logo-pixel bg-admin-label"></span>
                <span class="rounded-website-logo-pixel bg-admin-label"></span>
              </div>
              <p class="font-semibold text-primary-black">
                {{ t("home.steps.fix.task_title") }}
              </p>
            </div>
            <p class="mt-3.5 mb-5 text-sm/relaxed text-website-body">
              {{ t("home.steps.fix.task_body") }}
            </p>
            <div class="flex gap-3">
              <div
                class="flex min-h-11 grow items-center justify-center rounded-pill bg-brand-blue px-5 text-sm font-semibold text-white"
              >
                {{ t("home.steps.fix.activate") }}
              </div>
              <div
                class="flex min-h-11 grow items-center justify-center rounded-pill border border-website-task-border px-5 text-sm font-semibold text-primary-black"
              >
                {{ t("home.steps.fix.read_more") }}
              </div>
            </div>
          </div>
        </div>

        <!-- Step 4: Invite -->
        <div
          class="flex flex-col items-center gap-8 lg:flex-row-reverse lg:items-center lg:justify-center lg:gap-16"
        >
          <div class="flex w-full flex-col lg:w-lg lg:shrink-0">
            <span class="text-sm font-semibold text-brand-blue">{{
              t("home.steps.label", { number: 4 })
            }}</span>
            <h3
              class="mt-4 font-serif text-3xl font-medium text-primary-black md:text-4xl"
            >
              {{ t("home.steps.invite.title") }}
            </h3>
            <p class="mt-3.5 text-lg text-secondary-black">
              {{ t("home.steps.invite.body") }}
            </p>
          </div>
          <!-- Card: team members -->
          <div
            class="w-full rounded-2xl border border-brand-dark-beige bg-white p-6 shadow-card-prominent lg:w-108 lg:shrink-0"
          >
            <div class="mb-4 flex items-center justify-between gap-3">
              <p class="font-semibold text-primary-black">
                {{ t("home.steps.invite.card_title") }}
              </p>
              <span
                class="inline-flex h-8 shrink-0 items-center justify-center gap-1.5 rounded-pill bg-brand-blue px-3.5 text-base/none font-semibold whitespace-nowrap text-white"
              >
                <IconMaterialSymbolsAddRounded
                  class="size-4! shrink-0"
                  aria-hidden="true"
                />
                <span>{{ t("home.steps.invite.invite") }}</span>
              </span>
            </div>
            <div
              v-for="(member, index) in teamMembers"
              :key="member.initials"
              class="flex items-center gap-3 py-3"
              :class="
                index < teamMembers.length - 1
                  ? 'border-b border-card-line'
                  : ''
              "
            >
              <div
                class="flex size-9 shrink-0 items-center justify-center rounded-full text-website-detail font-semibold"
                :class="
                  index === 0
                    ? 'bg-blue-accent-light text-brand-blue'
                    : 'bg-card-line text-website-avatar-text'
                "
              >
                {{ member.initials }}
              </div>
              <div class="flex grow flex-col">
                <span class="font-semibold text-primary-black">{{
                  member.name
                }}</span>
                <span class="text-xs text-website-muted">{{
                  t(`home.steps.invite.${member.role}`)
                }}</span>
              </div>
              <span
                v-if="member.status === 'active'"
                class="flex h-6.5 items-center rounded-pill bg-website-status-active-bg px-3 text-xs font-semibold text-website-status-active-text"
                >{{ t("home.steps.invite.status_active") }}</span
              >
              <span
                v-else
                class="flex h-6.5 items-center rounded-pill bg-website-status-invited-bg px-3 text-xs font-semibold text-website-status-invited-text"
                >{{ t("home.steps.invite.status_invited") }}</span
              >
            </div>
          </div>
        </div>

        <!-- Step 5: Track -->
        <div
          class="flex flex-col items-center gap-8 lg:flex-row lg:items-center lg:justify-center lg:gap-16"
        >
          <div class="flex w-full flex-col lg:w-lg lg:shrink-0">
            <span class="text-sm font-semibold text-brand-blue">{{
              t("home.steps.label", { number: 5 })
            }}</span>
            <h3
              class="mt-4 font-serif text-3xl font-medium text-primary-black md:text-4xl"
            >
              {{ t("home.steps.track.title") }}
            </h3>
            <p class="mt-3.5 text-lg text-secondary-black">
              {{ t("home.steps.track.body") }}
            </p>
          </div>
          <!-- Card: progress -->
          <div
            class="flex w-full flex-col rounded-2xl border border-brand-dark-beige bg-white p-7 shadow-card-prominent lg:w-108 lg:shrink-0"
          >
            <p class="font-semibold text-primary-black">
              {{ t("home.steps.track.accounts_created") }}
            </p>
            <div
              class="mt-3.5 mb-2.5 h-3.5 overflow-clip rounded-pill bg-website-progress-track"
            >
              <div class="h-full w-3/4 bg-brand-green"></div>
            </div>
            <p class="flex items-baseline gap-1.5">
              <span class="text-lg font-semibold text-primary-black">9</span>
              <span class="text-sm text-website-muted">/ 12</span>
            </p>
            <div class="my-6 h-px w-full bg-card-line"></div>
            <p class="font-semibold text-primary-black">
              {{ t("home.steps.track.fully_protected") }}
            </p>
            <div
              class="mt-3.5 mb-2.5 h-3.5 overflow-clip rounded-pill bg-website-progress-track"
            >
              <div class="h-full w-half bg-brand-green"></div>
            </div>
            <p class="flex items-baseline gap-1.5">
              <span class="text-lg font-semibold text-primary-black">6</span>
              <span class="text-sm text-website-muted">/ 12</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- ═══════════════ More than a scan (dark) ═══════════════ -->
  <section class="w-full bg-website-dark-surface px-6 py-20 sm:py-24">
    <div class="mx-auto flex w-full max-w-7xl flex-col items-center">
      <h2
        class="max-w-5xl text-center text-4xl! leading-snug text-balance text-white! md:text-5xl!"
      >
        {{ t("home.more_than_scan.header") }}
      </h2>
      <p class="mt-6 max-w-3xl text-center text-lg text-white/80">
        {{ t("home.more_than_scan.body") }}
      </p>
      <div class="mt-16 grid w-full gap-12 md:grid-cols-3 md:gap-16">
        <div
          v-for="feature in moreFeatures"
          :key="feature.key"
          class="flex flex-col items-start text-left"
        >
          <span class="mb-5 flex h-8 items-center">
            <svg
              v-if="feature.shape === 'star'"
              viewBox="0 0 24 24"
              class="size-8"
              :fill="feature.color"
            >
              <path
                d="M12 0c1 8 4 11 12 12-8 1-11 4-12 12-1-8-4-11-12-12 8-1 11-4 12-12Z"
              />
            </svg>
            <svg
              v-else-if="feature.shape === 'triangle-up'"
              viewBox="0 0 24 24"
              class="size-8"
              :fill="feature.color"
            >
              <path d="M12 3 22 21 2 21Z" />
            </svg>
            <span
              v-else
              class="size-7 rounded-full"
              :style="{ backgroundColor: feature.color }"
            ></span>
          </span>
          <h3 class="mb-2.5 text-xl font-semibold text-white">
            {{ t(`home.more_than_scan.${feature.key}.header`) }}
          </h3>
          <p class="text-lg text-white/80">
            {{ t(`home.more_than_scan.${feature.key}.body`) }}
          </p>
        </div>
      </div>
    </div>
  </section>

  <!-- ═══════════════ White region: who benefits + blog + CTA ═══════════════ -->
  <!-- -mb-20 cancels the shared footer's mt-20 so the white section meets the
       footer directly instead of leaving a beige gap. -->
  <div class="-mb-20 w-full bg-white">
    <!-- Who benefits -->
    <section class="px-6 pt-20 pb-12 sm:pt-24 sm:pb-14">
      <div
        class="mx-auto flex w-full max-w-7xl flex-col gap-12 lg:flex-row lg:gap-16"
      >
        <div class="lg:w-2/5">
          <h2
            class="max-w-sm text-4xl! leading-snug text-balance text-primary-black! md:text-5xl!"
          >
            {{ t("home.who_benefits.header") }}
          </h2>
          <p class="mt-5 max-w-sm text-lg text-secondary-black">
            {{ t("home.who_benefits.note") }}
          </p>
        </div>
        <div class="flex flex-col lg:w-3/5">
          <div
            v-for="(item, index) in ['solo', 'growing', 'established']"
            :key="item"
            class="flex flex-col py-10"
            :class="
              index === 0
                ? 'lg:pt-0'
                : 'border-t border-website-section-divider'
            "
          >
            <h3 class="mb-2 text-xl font-semibold text-primary-black">
              {{ t(`home.who_benefits.${item}.header`) }}
            </h3>
            <p class="text-lg text-secondary-black">
              {{ t(`home.who_benefits.${item}.body`) }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <DecorStrip class="hidden sm:block" />

    <!-- From the Controla team -->
    <section class="px-6 pt-20 pb-24 sm:pt-24">
      <div class="mx-auto w-full max-w-7xl">
        <div class="mb-9 flex items-end justify-between gap-6">
          <h2 class="text-3xl! text-primary-black! md:text-4xl!">
            {{ t("home.from_the_team.header") }}
          </h2>
          <a
            :href="resourcesHref"
            class="hidden h-11 shrink-0 items-center rounded-pill border border-website-link-border px-5.5 text-website-copy font-semibold text-primary-black transition-colors hover:bg-brand-beige sm:inline-flex"
          >
            {{ t("home.from_the_team.view_all") }}
          </a>
        </div>
        <div class="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <a
            v-for="post in blogPosts"
            :key="post.routePath"
            :href="post.routePath"
            class="flex flex-col"
          >
            <img
              :srcset="post.headerImageSrcset"
              :src="post.headerImage"
              alt=""
              loading="lazy"
              decoding="async"
              width="614"
              height="346"
              class="aspect-video w-full rounded-2xl object-cover"
            />
            <span
              class="mt-4 text-website-detail font-semibold text-website-muted capitalize"
              >{{ post.category }}</span
            >
            <p
              class="mt-2 text-website-feature-title/snug font-semibold text-primary-black"
            >
              {{ post.title }}
            </p>
          </a>
        </div>
      </div>
    </section>

    <!-- Closing CTA banner (shared design with product & partners pages) -->
    <section class="px-6 pt-20 pb-24 sm:pt-28 sm:pb-32">
      <div class="mx-auto max-w-7xl">
        <div
          class="relative flex flex-col items-center gap-6 rounded-3xl bg-blue-accent-light p-8 text-center lg:flex-row lg:items-center lg:justify-between lg:gap-8 lg:px-14 lg:py-10 lg:pr-64 lg:text-left"
        >
          <div class="flex flex-col gap-2">
            <h2 class="text-2xl! sm:text-3xl!">
              {{ t("home.final_cta.header") }}
            </h2>
            <p class="text-lg text-body-text">
              {{ t("home.final_cta.body") }}
            </p>
          </div>
          <CTAButton
            :message="t('cta.start_trial')"
            :link="appBase"
            link-medium="home"
            link-campaign="final_cta"
            link-class="!bg-brand-blue !px-8 !text-base shrink-0 whitespace-nowrap"
          />
          <img
            :src="mascotImage"
            alt=""
            class="pointer-events-none absolute right-14 bottom-0 hidden h-website-callout-artwork w-auto lg:block"
          />
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped lang="css"></style>
